import { useEffect, useMemo, useState } from 'react'
import {
  listStudents,
  archiveStudent,
  deleteStudent,
  STATUS_OPTIONS,
  FEE_STATUS_OPTIONS,
  GENDER_OPTIONS,
  DATE_PERIOD_OPTIONS,
} from '../api/studentsApi'
import { loadMasterOptions } from '../api/mastersApi'
import { appConfirm } from '../../../../components/AppConfirmModal'
import { appToast } from '../../../../components/AppToast'

const DEFAULT_PAGE_SIZE = 10

export function useStudentsListScreen() {
  // Basic filters
  const [searchField, setSearchField] = useState('Name')
  const [searchText, setSearchText] = useState('')
  const [status, setStatus] = useState('All')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)

  // Education reference filters
  const [company, setCompany] = useState('All')
  const [branch, setBranch] = useState('All')
  const [course, setCourse] = useState('All')
  const [batch, setBatch] = useState('All')
  const [batch2, setBatch2] = useState('All')
  const [scheme, setScheme] = useState('All')
  const [academicYear, setAcademicYear] = useState('All')
  const [datePeriod, setDatePeriod] = useState('All')
  const [fromDate, setFromDate] = useState('')
  const [toDate, setToDate] = useState('')
  const [subject, setSubject] = useState('All')
  const [subject2, setSubject2] = useState('All')
  const [counsellor, setCounsellor] = useState('All')
  const [gender, setGender] = useState('All')
  const [feeStatus, setFeeStatus] = useState('All')

  const [masterOptions, setMasterOptions] = useState({})

  useEffect(() => {
    loadMasterOptions([
      'institutes', 'branches', 'courses', 'batches',
      'schemes', 'academic_years', 'subjects', 'counsellors',
    ]).then(setMasterOptions).catch(() => {})
  }, [])

  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')
  const [items, setItems] = useState([])
  const [meta, setMeta] = useState({
    totalCount: 0,
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    totalPages: 1,
  })

  const queryParams = useMemo(
    () => ({
      searchField,
      searchText,
      status,
      company,
      branch,
      course,
      batch,
      batch2,
      scheme,
      academicYear,
      datePeriod,
      fromDate,
      toDate,
      subject,
      subject2,
      counsellor,
      gender,
      feeStatus,
    }),
    [searchField, searchText, status, company, branch, course, batch, batch2, scheme, academicYear, datePeriod, fromDate, toDate, subject, subject2, counsellor, gender, feeStatus],
  )

  const loadStudents = async () => {
    setIsLoading(true)
    setError('')
    try {
      const allStudents = await listStudents({ ...queryParams, page, pageSize })
      const totalCount = allStudents._total_count || allStudents.length
      const startIndex = (page - 1) * pageSize
      const paginated = allStudents._total_count != null
        ? allStudents
        : allStudents.slice(startIndex, startIndex + pageSize)
      setItems(paginated)
      setMeta({
        totalCount,
        page,
        pageSize,
        totalPages: Math.max(1, Math.ceil(totalCount / pageSize)),
      })
    } catch {
      setError('Failed to load students.')
      setItems([])
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    loadStudents()
  }, [queryParams, page, pageSize])

  const resetFilters = () => {
    setSearchField('Name')
    setSearchText('')
    setStatus('All')
    setCompany('All')
    setBranch('All')
    setCourse('All')
    setBatch('All')
    setBatch2('All')
    setScheme('All')
    setAcademicYear('All')
    setDatePeriod('All')
    setFromDate('')
    setToDate('')
    setSubject('All')
    setSubject2('All')
    setCounsellor('All')
    setGender('All')
    setFeeStatus('All')
    setPage(1)
  }

  const handleArchive = async (studentId) => {
    try {
      await archiveStudent(studentId)
      await loadStudents()
    } catch {
      setError('Failed to update student status.')
    }
  }

  const handleDelete = async (studentId) => {
    const confirmed = await appConfirm({
      title: 'Delete Student',
      message: 'Are you sure you want to permanently delete this student record? This action cannot be undone.',
      confirmText: 'Delete Student',
      variant: 'danger',
    })
    if (!confirmed) return

    try {
      await deleteStudent(studentId)
      appToast.success('Student deleted successfully')
      await loadStudents()
    } catch {
      appToast.error('Failed to delete student.')
      setError('Failed to delete student.')
    }
  }

  const resetPage = () => setPage(1)

  return {
    state: {
      searchField,
      searchText,
      status,
      company,
      branch,
      course,
      batch,
      batch2,
      scheme,
      academicYear,
      datePeriod,
      fromDate,
      toDate,
      subject,
      subject2,
      counsellor,
      gender,
      feeStatus,
      page,
      pageSize,
      isLoading,
      error,
      rows: items,
      meta,
      options: {
        statusOptions: STATUS_OPTIONS,
        companyOptions: ['All', ...(masterOptions.institutes || [])],
        branchOptions: ['All', ...(masterOptions.branches || [])],
        courseOptions: ['All', ...(masterOptions.courses || [])],
        batchOptions: ['All', ...(masterOptions.batches || [])],
        batch2Options: ['All', ...(masterOptions.batches || [])],
        schemeOptions: ['All', ...(masterOptions.schemes || [])],
        academicYearOptions: ['All', ...(masterOptions.academic_years || [])],
        datePeriodOptions: DATE_PERIOD_OPTIONS,
        subjectOptions: ['All', ...(masterOptions.subjects || [])],
        subject2Options: ['All', ...(masterOptions.subjects || [])],
        counsellorOptions: ['All', ...(masterOptions.counsellors || [])],
        genderOptions: GENDER_OPTIONS,
        feeStatusOptions: FEE_STATUS_OPTIONS,
        pageSizeOptions: [10, 25, 50, 75, 100, 250, 500],
      },
    },
    actions: {
      setSearchField,
      setSearchText: (v) => { setSearchText(v) },
      setStatus: (v) => { setStatus(v); resetPage() },
      setCompany: (v) => { setCompany(v); resetPage() },
      setBranch: (v) => { setBranch(v); resetPage() },
      setCourse: (v) => { setCourse(v); resetPage() },
      setBatch: (v) => { setBatch(v); resetPage() },
      setBatch2: (v) => { setBatch2(v); resetPage() },
      setScheme: (v) => { setScheme(v); resetPage() },
      setAcademicYear: (v) => { setAcademicYear(v); resetPage() },
      setDatePeriod: (v) => { setDatePeriod(v); resetPage() },
      setFromDate: (v) => { setFromDate(v) },
      setToDate: (v) => { setToDate(v) },
      setSubject: (v) => { setSubject(v); resetPage() },
      setSubject2: (v) => { setSubject2(v); resetPage() },
      setCounsellor: (v) => { setCounsellor(v); resetPage() },
      setGender: (v) => { setGender(v); resetPage() },
      setFeeStatus: (v) => { setFeeStatus(v); resetPage() },
      setPage,
      setPageSize: (v) => { setPageSize(Number(v)); setPage(1) },
      resetFilters,
      handleArchive,
      handleDelete,
      refresh: loadStudents,
    },
  }
}
